import debounce from 'lodash.debounce'
// eslint-disable-next-line import/no-cycle
import { fetchBi } from '../../network'
// eslint-disable-next-line import/no-cycle
import { logException } from '../wscjs/analytics'
import { MetaData } from './types/metaData'
import { createGuid } from '../../utils'
import { AuditEventDTO } from './types/auditEventDTO'
import { AuditEvent } from './types/auditEvent'
import { EventProperties } from './types/eventProperties'

// Send the accumulated events to the backend before page unload, as they might not be sent because of the debounce
window.addEventListener('beforeunload', sendEventsBatchProcess)

let metaData: MetaData
let eventsAccumulator: AuditEvent[] = []
const sendEventsHandler = debounce(sendEventsBatchProcess, 2000)

function sendEventsBatchProcess() {
  if (!eventsAccumulator.length) {
    return
  }

  const events = eventsAccumulator.map((trackEvent) => convertToEventPayload(trackEvent))

  sendEventsBatch(events)

  eventsAccumulator = []
}

export function addEvent({
  eventName,
  eventProperties,
}: {
  eventName: string
  eventProperties: EventProperties
}) {
  eventsAccumulator.push({ eventName, eventProperties, unixTime: Date.now() })

  sendEventsHandler()
}

export function sendEvent({
  eventName,
  eventProperties,
}: {
  eventName: string
  eventProperties: EventProperties
}) {
  const eventPayload = convertToEventPayload({ eventName, eventProperties, unixTime: Date.now() })

  sendEventsBatch([eventPayload])
}

async function sendEventsBatch(events: AuditEventDTO[]) {
  try {
    await fetchBi('/Bi/UploadToAzure', {
      method: 'POST',
      body: { metaData, events },
      keepalive: true, // Allow the request to outlive the page, because we want to send analytics data requests even after app's unmount
      preventUnauthorizedLogout: true,
    })
  } catch (error) {
    // No retry mechanism - If the request failed, the event won't be added to WSC internal data warehouse.
    logException(error, { metaData, events })
  }
}

export function initiliazeMetaData(userInfo: {
  customerName: string
  customerRole: string
  userAliasName: string
  userGroupId: string
  userGroupName: string
  systemRole: string
  systemName: string
  wscWorkerId: string
}) {
  const urlSearchParams = new URLSearchParams(window.location.search)

  metaData = {
    ...userInfo,
    sessionId: createGuid(),
    userAgent: navigator.userAgent,
    initReferrer: document.referrer?.length > 0 ? document.referrer : undefined,
    utmSource: urlSearchParams.get('utm_source') ?? undefined,
    utmMedium: urlSearchParams.get('utm_medium') ?? undefined,
    utmCampaign: urlSearchParams.get('utm_campaign') ?? undefined,
    utmTerm: urlSearchParams.get('utm_term') ?? undefined,
    utmContent: urlSearchParams.get('utm_content') ?? undefined,
    utmId: urlSearchParams.get('utm_id') ?? undefined,
  }
}

function convertToEventPayload({
  eventName,
  eventProperties,
  unixTime,
}: AuditEvent): AuditEventDTO {
  const unixTimeSeconds = Math.floor(unixTime / 1000)

  const dateIsoFormat = new Date(unixTimeSeconds * 1000).toISOString()

  const eventPropertiesString = eventProperties ? JSON.stringify(eventProperties) : null

  return {
    eventName,
    eventProperties: eventPropertiesString,
    time: unixTimeSeconds,
    timeIso: dateIsoFormat,
    product: eventProperties.product,
    currentUrl: window.location.href,
    screenHeight: window.screen.height.toString(),
    screenWidth: window.screen.width.toString(),
    referrer: document.referrer?.length > 0 ? document.referrer : undefined,
  }
}
